import BasePage from 'components/pages/BasePage/BasePage'
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import './SearchPage.scss'
import AssetFeed from 'components/feeds/AssetFeed/AssetFeed'
import { useDispatch } from 'react-redux'
import { BreadCrumbPriority, replaceCrumb, useSearch, ZeroStateToShowEnum, IFilterAction } from 'store'
import FilterContainer from 'components/pages/SearchPage/filters/FilterContainer'
import { Grid, Chip } from '@enterprise-ui/canvas-ui-react'
import SearchBar from 'components/SearchBar/SearchBar'
import { convertToTitle } from 'utils/string'
import { some } from 'lodash'
import AiKeywordsChips from './AiKeywordsChips'

const SearchPage = () => {
  const location = useLocation()
  const {
    searchPageAssetsAreLoading: isLoading,
    searchPageAssets,
    currentSearchValue,
    searchError,
    setSearchError,
    infiniteScrollMethod,
    querySearchCriteria,
    dispatchSearch,
    setSearchTab,
    setSearchValue,
    clearStore,
    updateSearchCriteriaFilters,
    currentFilterChips,
    aiSearchToggle,
    aiKeywords,
    setAiAndOrKeywords,
    exactSearch,
    selfAssetToggle,
    activateSearchChange,
  } = useSearch()
  const dispatch = useDispatch()
  const crumb = {
    name: 'Search',
    href: `/search`,
    search: location.search,
    priority: BreadCrumbPriority.High,
  }
  const [isSearchHandleChange, setIsSearchHandleChange] = useState<boolean>(false)
  const [isExactSearchOrSelfAssetToggle, setIsExactSearchOrSelfAssetToggle] = useState<boolean>(false)
  const [searchInputChanged, setSearchInputChanged] = useState(false)
  const { andKeywords, orKeywords } = useMemo(() => aiKeywords || {}, [aiKeywords])
  const [chipsChanged, setChipsChanged] = useState(false)

  useEffect(() => {
    const searchCrumb = {
      name: 'Search',
      href: `/search`,
      search: location.search,
      priority: BreadCrumbPriority.High,
    }
    dispatch(replaceCrumb(searchCrumb))
  }, [location.search])

  useEffect(() => {
    if (!isSearchHandleChange && !currentSearchValue && querySearchCriteria.q !== currentSearchValue) {
      setIsExactSearchOrSelfAssetToggle(true)
    }
  }, [isSearchHandleChange, currentSearchValue, querySearchCriteria.q])

  // updates search results after deleting asset and also provide deep linking
  useEffect(() => {
    if (isExactSearchOrSelfAssetToggle) {
      clearStore()
      dispatchSearch({
        q: querySearchCriteria.q as string | undefined,
        p: 0,
        f: querySearchCriteria.f as { [key: string]: string[] } | undefined,
        t: querySearchCriteria.t as string | undefined,
        size: querySearchCriteria.size as number | undefined,
      })
      setSearchTab((querySearchCriteria.t as string) || 'All')
      setSearchValue((querySearchCriteria.q as string) || '')
      activateSearchChange(false)
      setIsExactSearchOrSelfAssetToggle(false)
      return () => {
        setSearchError(false)
      }
    }
  }, [querySearchCriteria.q, exactSearch, selfAssetToggle, isExactSearchOrSelfAssetToggle])

  // This feature was causing some issues with state being out of sync between filters and search queries. Opting to leave this out until we have time to work out a solve.
  // useEffect(() => {
  //   dispatchSearch({
  //     q: querySearchCriteria.q as string | undefined,
  //     p: 0,
  //     f: querySearchCriteria.f as { [key: string]: string[] } | undefined,
  //     t: querySearchCriteria.t as string | undefined,
  //     size: querySearchCriteria.size as number | undefined,
  //   })
  // }, [exactSearch])

  return (
    <BasePage crumbs={crumb}>
      <Grid.Container spacing="none">
        <Grid.Item xs={5} sm={5} md={4} lg={3} xl={3} className="filters hc-pl-normal hc-pt-none hc-pb-none">
          <FilterContainer />
        </Grid.Item>
        <Grid.Item xs className="asset-feed-content hc-pa-none">
          <Grid.Container className="with-filters heading" direction="column" justify="space-around">
            <Grid.Item xs={12} className="hc-pt-none">
              <Grid.Container justify="center" align="center">
                <Grid.Item className="hc-pt-none">
                  <SearchBar
                    className="search-page-search"
                    data-testid="search-page-overview"
                    showSelect={true}
                    setIsSearchHandleChange={setIsSearchHandleChange}
                    setIsExactSearchOrSelfAssetToggle={setIsExactSearchOrSelfAssetToggle}
                    searchInputChanged={searchInputChanged}
                    setSearchInputChanged={setSearchInputChanged}
                    chipsChanged={chipsChanged}
                    setChipsChanged={setChipsChanged}
                  />
                </Grid.Item>
              </Grid.Container>
            </Grid.Item>
            {aiSearchToggle && ((andKeywords || []).length > 0 || (orKeywords || []).length > 0) && (
              <Grid.Item xs={12} className="hc-mb-2x">
                <Grid.Container align="center" justify="center" className="container-width-control">
                  <Grid.Item xs={12} className="hc-pa-none">
                    <div className="appear">
                      <AiKeywordsChips
                        andKeywords={andKeywords}
                        orKeywords={orKeywords}
                        setAiAndOrKeywords={setAiAndOrKeywords}
                        setIsExactSearchOrSelfAssetToggle={setIsExactSearchOrSelfAssetToggle}
                        setChipsChanged={setChipsChanged}
                      />
                    </div>
                  </Grid.Item>
                </Grid.Container>
              </Grid.Item>
            )}
            {currentFilterChips && some(Object.keys(currentFilterChips)) && (
              <Grid.Item xs={12}>
                <div className="container-width-control">
                  {Object.keys(currentFilterChips)!!.map((currentFilterChip: string, index) => {
                    const subFilters = currentFilterChips[currentFilterChip]
                    if (currentFilterChip === 'original_upload_date') {
                      const strigifiedDate = JSON.stringify(subFilters[0])
                      const parsedDate = JSON.parse(strigifiedDate)
                      const startDate = parsedDate?.startDate
                      const endDate = parsedDate?.endDate

                      if (startDate && endDate) {
                        return (
                          <Chip
                            key={`${index}-search-results-chip`}
                            role="false"
                            data-testid="gallery-chip"
                            className="gallery-chips-dense hc-mb-xs"
                            onRequestDelete={() => {
                              updateSearchCriteriaFilters(IFilterAction.Remove, currentFilterChip)
                            }}
                          >
                            <span className="bold hc-ma-none hc-pl-dense hc-pr-none hc-fs-xs">
                              {convertToTitle(currentFilterChip, '_')}:
                            </span>
                            <span className="hc-pa-none">
                              {' ' + startDate.trim().toUpperCase() + ' - ' + endDate.trim().toUpperCase()}
                            </span>
                          </Chip>
                        )
                      }
                    } else {
                      return subFilters.map(subFilter => (
                        <Chip
                          key={`${index}-search-results-chip`}
                          role="false"
                          data-testid="gallery-chip"
                          className="gallery-chips-dense hc-mb-xs"
                          onRequestDelete={() => {
                            updateSearchCriteriaFilters(IFilterAction.Remove, currentFilterChip, subFilter)
                          }}
                        >
                          <span className="bold hc-ma-none hc-pl-dense hc-pr-none hc-fs-xs">
                            {convertToTitle(currentFilterChip, '_')}:
                          </span>
                          <span className="hc-pa-none">{' ' + subFilter.trim().toUpperCase()}</span>
                        </Chip>
                      ))
                    }
                    return null
                  })}
                </div>
              </Grid.Item>
            )}
            <AssetFeed
              pageName="searchPage"
              isLoading={isLoading}
              assets={searchPageAssets.assets}
              totalCount={searchPageAssets.total}
              zeroStateToShow={ZeroStateToShowEnum.SEARCHPAGE}
              infiniteScrollMethod={infiniteScrollMethod}
              disableInfinite={searchError}
              showResultsLabel
            />
          </Grid.Container>
        </Grid.Item>
      </Grid.Container>
    </BasePage>
  )
}

export default SearchPage
